import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { ArrowDownAZ, ArrowUpAZ, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import AdminLayout from '../components/admin/AdminLayout';
import workspaceService from '../services/workspaceService';
import '../assets/css/superAdminWorkspaceManagement.scss';

export default function SuperAdminWorkspaceManagement() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredWorkspaces = useMemo(() => {
    const q = workspaceSearch.trim().toLowerCase();
    let list = workspaces;
    if (q) {
      list = workspaces.filter((workspace) =>
        String(workspace.workspaceName || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [workspaces, workspaceSearch, sortOrder]);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await workspaceService.listWorkspaces();
      if (response?.success && Array.isArray(response?.data)) {
        setWorkspaces(response.data);
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error('Access denied. Super Admin only.', {
          className: 'bg-secondary-500 text-white',
        });
        navigate('/dashboard');
        return;
      }
      console.error('Error fetching workspaces:', error);
      toast.error(error?.message || 'Failed to fetch workspaces', {
        className: 'bg-secondary-500 text-white',
      });
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = object({
    workspaceName: string()
      .required('Workspace name is required')
      .min(3, 'Workspace name must be at least 3 characters'),
    adminEmail: string()
      .email('Invalid email address')
      .required('Admin email is required')
      .test('domain', 'Invalid email address', (value) => {
        if (!value) return false;
        const domainPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
        return domainPattern.test(value);
      }),
  });

  const formik = useFormik({
    initialValues: { workspaceName: '', adminEmail: '' },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsCreatingWorkspace(true);
        const response = await workspaceService.createWorkspace(
          values.workspaceName,
          values.adminEmail,
        );

        if (response?.success) {
          const existingAdmin = response?.data?.existingAdmin;
          toast.success(
            existingAdmin
              ? 'Workspace created. Existing admin was assigned and notified by email.'
              : 'Workspace created successfully! Invitation sent to admin.',
            {
              className: 'bg-green-500 text-white',
            },
          );
          formik.resetForm();
          setShowCreateForm(false);
          fetchWorkspaces();
        }
      } catch (error) {
        console.error('Error creating workspace:', error);
        toast.error(error?.message || 'Failed to create workspace', {
          className: 'bg-secondary-500 text-white',
        });
      } finally {
        setIsCreatingWorkspace(false);
      }
    },
  });

  const getStatusBadge = (status) => {
    const isActive = status === 'completed';
    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-pending'}`}>
        {isActive ? 'Active' : 'Pending'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <AdminLayout title="workspace management">
      <div className="px-6 py-4">
        {showCreateForm && (
          <div className="create-workspace-form">
            <h2>Create New Workspace</h2>
            <form onSubmit={formik.handleSubmit}>
              <div className="form-group">
                <label htmlFor="workspaceName">Workspace Name</label>
                <input
                  type="text"
                  id="workspaceName"
                  name="workspaceName"
                  placeholder="e.g., Trigent"
                  value={formik.values.workspaceName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isCreatingWorkspace}
                  className={formik.touched.workspaceName && formik.errors.workspaceName ? 'error' : ''}
                />
                {formik.touched.workspaceName && formik.errors.workspaceName && (
                  <span className="error-text">{formik.errors.workspaceName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="adminEmail">Admin Email</label>
                <input
                  type="email"
                  id="adminEmail"
                  name="adminEmail"
                  placeholder="admin@company.com"
                  value={formik.values.adminEmail}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isCreatingWorkspace}
                  className={formik.touched.adminEmail && formik.errors.adminEmail ? 'error' : ''}
                />
                {formik.touched.adminEmail && formik.errors.adminEmail && (
                  <span className="error-text">{formik.errors.adminEmail}</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isCreatingWorkspace || !formik.isValid}
                  aria-busy={isCreatingWorkspace}
                >
                  {isCreatingWorkspace ? 'Creating...' : 'Create & Invite'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateForm(false);
                    formik.resetForm();
                  }}
                  disabled={isCreatingWorkspace}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {!showCreateForm && (
          <div className="workspaces-list">
            <div className="workspaces-list-header">
              <h2>Your Workspaces : {workspaces.length}</h2>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
              >
                + New Workspace
              </button>
            </div>

            {workspaces.length > 0 && (
              <div className="workspace-list-toolbar">
                <div className="workspace-search-wrap">
                  <Search className="workspace-search-icon" aria-hidden />
                  <input
                    type="text"
                    value={workspaceSearch}
                    onChange={(e) => setWorkspaceSearch(e.target.value)}
                    placeholder="Search by company name..."
                    className="workspace-search-input"
                  />
                </div>
                <button
                  type="button"
                  className="workspace-sort-btn"
                  onClick={() => setSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'))}
                  title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
                >
                  {sortOrder === 'desc' ? (
                    <ArrowDownAZ className="w-4 h-4" aria-hidden />
                  ) : (
                    <ArrowUpAZ className="w-4 h-4" aria-hidden />
                  )}
                  <span>Date {sortOrder === 'desc' ? '(newest)' : '(oldest)'}</span>
                </button>
              </div>
            )}

            {loading ? (
              <div className="loading-state">
                <p>Loading workspaces...</p>
              </div>
            ) : filteredWorkspaces.length > 0 ? (
              <div className="workspaces-grid">
                {filteredWorkspaces.map((workspace) => (
                  <div key={workspace.workspaceId} className="workspace-card">
                    <div className="card-header">
                      <h3>{workspace.workspaceName}</h3>
                      <span className="host-badge">{workspace.host}</span>
                    </div>

                    <div className="card-body">
                      <div className="workspace-info">
                        <label>Created:</label>
                        <span>{formatDate(workspace.createdAt)}</span>
                      </div>

                      {workspace.createdBy && (workspace.createdBy.name || workspace.createdBy.email) ? (
                        <div className="workspace-info">
                          <label>Created By:</label>
                          <span>
                            {workspace.createdBy.name || workspace.createdBy.email}
                            {workspace.createdBy.name && workspace.createdBy.email
                              ? ` (${workspace.createdBy.email})`
                              : ''}
                          </span>
                        </div>
                      ) : null}

                      <div className="workspace-info">
                        <label>Assigned Admins:</label>
                        {workspace.admins && workspace.admins.length > 0 ? (
                          <div className="admins-list">
                            {workspace.admins.map((admin, idx) => (
                              <div key={idx} className="admin-item">
                                <span className="admin-email">{admin.email}</span>
                                {getStatusBadge(admin.status)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="no-admins">No admins assigned</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : workspaces.length > 0 ? (
              <div className="empty-state">
                <p>No workspaces match “{workspaceSearch.trim()}”.</p>
              </div>
            ) : (
              <div className="empty-state">
                <p>No workspaces yet. Create one to get started!</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create First Workspace
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
